import React, { useState, useEffect, useRef } from 'react';
import MazTuInfoBeesButton from '../../../components/maz-tu-info-bees/maz-tu-info-bees-button/MazTuInfoBeesButton';
import { Snackbar, Alert, AlertTitle } from '@mui/material';
import './ContratoFirma.scss';

import { retry } from '../../../utils/retry';
import { on } from '../../../utils/events';
import logData from '../../../utils/logger';

export default function ContratoFirma(props) {
  const [personaType] = useState('fisica');
  const [validation, setValidation] = useState(true);
  const [urlContract, setUrlContract] = useState('');
  const [iframeOpen, setIframeOpen] = useState(false);

  const intervalRef = useRef(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message1: '',
    message2: '',
    severity: 'info', // error | warning | success | info
    time: 5000,
  });

  /*  ======== componentDidMount  ========*/
  useEffect(() => {
    try {
      const handleProcess = props.handleProcess;

      on('click-back-navigation-programa-mercado', function () {
        handleProcess({
          protocol: 'PATCH',
          params: {
            transition: 'revertSigning',
          },
        });
      });
    } catch (e) {
      console.log(e);
    }

    // componentWillUnmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  /*  ======== IF en render  ========*/
  useEffect(() => {
    if (validation && props.dataProcess !== null) {
      props.setLoaderOpen(true);
      getContractSign();
    }
  }, [validation, props.dataProcess]);

  const getContractSign = () => {
    const storedUserData = localStorage.getItem('@selectType');
    let decoded = null;
    try { decoded = storedUserData ? JSON.parse(storedUserData) : null; } catch (_) {}

    // Fallback: si localStorage se perdió, recuperar typeGoal desde los datos del proceso (backend)
    if (!decoded) {
      const typeGoal =
        props.dataProcess?.typeGoal ||
        props.dataProcess?.responseData?.process?.contractData?.personalInfo?.typeGoal ||
        props.dataProcess?.responseData?.currentStep?.stepData?.typeGoal;
      if (typeGoal) {
        decoded = { type: typeGoal };
        localStorage.setItem('@selectType', JSON.stringify(decoded));
      }
    }

    const hasContract = !!props.dataProcess.contractData?.tratoId;

    setValidation(false);
    props.setLoaderOpen(true);

    if (hasContract) {
      getStatusSign();
      getSignature();
    } else {
      let procId = props.dataProcess.responseData?.process?.processId
        ? props.dataProcess.responseData.process.processId
        : props.dataProcess.processId;
      
      logData({ step: 'before_get_contract_sign', extra: { procId, decoded } });

      if (procId && decoded) {
        let counter = 0;
        const maxRetries = 2;
        const time = 7000;
        const options = { retries: maxRetries, retryIntervalMs: time };
        retry(() => {
          if (counter > 0) {
            showSnackbar(
              'Problemas al conectar con el servicio.',
              `Reintentando. (${counter}/${maxRetries})`,
              'warning',
              time - 750
            );
          }

          counter++;
          return new Promise((resolve, reject) => {
            props.handleProcess({
              protocol: 'POST',
              isCreateContract: true,
              params: {
                processId: procId,
                identificator: decoded.type,
              },
              callbackSuccess: (response) => {
                try {
                  if (response.data.contractId !== '') {
                    getSignature();
                    resolve();
                  } else {
                    logData({ step: 'contract_created_empty_id', extra: { response } });
                    reject(new Error('contractId vacío'));
                  }
                } catch (err) {
                  props.setLoaderOpen(false);
                  logData({ step: 'contract_create_success_catch', error: err });
                  reject(err);
                }
              },
            });
          });
        }, options).catch((err) => {
          logData({ step: 'contract_create_failed', error: err });
          returnToUpdateInfo(time);
        });
      } else {
        const time = 7000;
        logData({ step: 'contract_create_failed'});
        returnToUpdateInfo(time);
      }
    }
  };

  const getSignature = () => {
    let processId;

    try {
      processId = props.dataProcess.responseData.process.processId;
    } catch (e) {
      processId = props.dataProcess.processId;
    }

    props.setLoaderOpen(true);

    props.handleProcess({
      protocol: 'POST',
      isSignature: true,
      params: {
        processId,
      },
      callbackSuccess: (response) => {
        props.setLoaderOpen(false);
        try {
          console.log(response);
          setUrlContract(response.data.signUrl);
        } catch (e) {
          console.log(e);
        }
      },
    });
  };

  const getStatusSign = () => {
    intervalRef.current = setInterval(() => {
      props.handleProcess({
        loader: false,
        protocol: 'GET',
        isStatusSignature: true,
        callbackSuccess: (response) => {
          try {
            console.log(response);
            if (response.data.documentSigned) {
              clearInterval(intervalRef.current);

              props.handleProcess({
                protocol: 'PATCH',
                params: {
                  transition: 'completeProcess',
                },
              });
            }
          } catch (e) {
            console.log(e);
          }
        },
      });
    }, 20000);
  };

  const handleButton = () => {
    getStatusSign();
    window.open(urlContract, '_blank');
  };

  const showSnackbar = (message1, message2, severity = 'info', time = 5000) => {
    setSnackbar({
      open: true,
      message1,
      message2,
      severity,
      time,
    });
  };

  const returnToUpdateInfo = (time) => {
    setTimeout(() => {
      showSnackbar('Problemas al generar el contrato.', 'Te regresaremos para intentar nuevamente.', 'error', time - 750);
    }, time);
    setTimeout(() => {
      props.handleProcess({
        protocol: 'PATCH',
        params: {
          transition: 'revertSigning',
        },
      });
    }, time * 2);
  };

  const iframeState = iframeOpen ? 'open' : 'close';

  return (
    <div className="contrato-firma">
      <div className="contrato-firma__title">Firma de contrato</div>

      <div className="contrato-firma__message">
        Da clic en el botón para abrir la ventana del contrato y sigue las instrucciones.
        <br />
        <br />
        Una vez firmado, esta pantalla se actualizará para terminar el proceso de la firma.
      </div>

      <div className="contrato-firma__button">
        <MazTuInfoBeesButton
          disabled={urlContract === ''}
          textButton={urlContract === '' ? 'OBTENIENDO CONTRATO' : 'FIRMAR CONTRATO'}
          handleClick={handleButton}
        />
      </div>

      <div className={'contrato-firma__iframe contrato-firma__iframe--' + iframeState}>
        <iframe src={urlContract} title="iframe-signature" crossOrigin="anonymous" />
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.time}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message1 && <AlertTitle>{snackbar.message1}</AlertTitle>}
          {snackbar.message2}
        </Alert>
      </Snackbar>
    </div>
  );
}
